'use client';

import { Avatar, Button } from 'antd';
import classNames from 'classnames';
import { BadgeInfo, House, LogOut, Newspaper, Store } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useCallback, useLayoutEffect, useMemo, useRef, useState, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { If } from '@components/condition';
import { IconMenu } from '@components/shared';
import { AuthModal } from '@features/auth/AuthModal';
import { useHeaderVisibility, useIsMobile } from '@hooks/index';
import { modalActions, userActions } from '@store/actions';
import { MenuItem } from '@type/menu';

import MenuMobile from './MenuMobile';

export const defaultMenuItems: MenuItem[] = [
  {
    key: '/',
    href: '/',
    label: 'Trang chủ',
    icon: <House className="w-5 h-5" strokeWidth={2.5} />,
  },
  {
    key: '/san-pham',
    href: '/san-pham',
    label: 'Sản phẩm',
    icon: <Store className="w-5 h-5" strokeWidth={2.5} />,
  },
  {
    key: '/gioi-thieu',
    href: '/gioi-thieu',
    label: 'Giới thiệu',
    icon: <BadgeInfo className="w-5 h-5" strokeWidth={2.5} />,
  },
  {
    key: '/tin-tuc',
    href: '/tin-tuc',
    label: 'Tin tức',
    icon: <Newspaper className="w-5 h-5" strokeWidth={2.5} />,
  },
];

interface MainHeaderProps {
  theme?: 'light' | 'dark';
  showAuthButtons?: boolean;
  customMenuItems?: MenuItem[];
}

const MainHeaderContent = ({
  theme = 'light',
  showAuthButtons = true,
  customMenuItems,
}: MainHeaderProps) => {
  const isMobile = useIsMobile(1024);
  const [collapsed, setCollapsed] = useState(false);
  const router = useRouter();
  const itemRefs = useRef<(HTMLAnchorElement | null)[]>([]);
  const showHeader = useHeaderVisibility();
  const dispatch = useDispatch();
  const { user } = useSelector((state: any) => state.user);
  const items = useMemo(() => customMenuItems || defaultMenuItems, [customMenuItems]);

  const isActive = useCallback(
    (menuKey: string) => {
      if (router?.pathname.includes('ho-so') || router?.pathname.includes('dat-hang')) return false;
      return menuKey === '/' ? router.pathname === '/' : router.pathname.startsWith(menuKey);
    },
    [router.pathname]
  );

  const activeIndex = useMemo(
    () => items.findIndex((menu) => isActive(menu.key)),
    [items, isActive]
  );

  const menuItemClasses = useCallback(
    (active: boolean) =>
      classNames(
        'font-[600] px-2 py-1 w-[100px] text-center rounded-md z-10 transition-all duration-300',
        {
          'text-gray-800 hover:text-orange-600': theme === 'light' && !active,
          'text-gray-300 hover:text-white': theme === 'dark' && !active,
          'text-white': active,
        }
      ),
    [theme]
  );

  const [activeBar, setActiveBar] = useState({ width: 0, x: 0 });

  useLayoutEffect(() => {
    const el = itemRefs.current[activeIndex];
    if (el) {
      const rect = el.getBoundingClientRect();
      const containerRect = el.parentElement?.getBoundingClientRect();
      if (containerRect) {
        setActiveBar({ width: rect.width, x: rect.left - containerRect.left });
      }
    }
  }, [activeIndex, isMobile]);

  const headerClasses = useMemo(
    () =>
      classNames('w-full fixed top-0 left-0 z-[100] transition-all duration-300', {
        'bg-white border-b border-gray-200 shadow-headerShadow': theme === 'light',
        'bg-gray-900 border-b border-gray-800': theme === 'dark',
        'translate-y-0 opacity-100': showHeader,
        '-translate-y-full opacity-0 pointer-events-none': !showHeader,
      }),
    [theme, showHeader]
  );

  const contentStyle = useMemo(() => ({ paddingTop: '58px' }), []);

  const handleOpenAuthModal = useCallback(() => {
    dispatch(
      modalActions.openModal({
        modalId: 'auth-modal',
        modalComponent: <AuthModal modalId="auth-modal" />,
      })
    );
  }, [dispatch]);

  const handleLogout = useCallback(() => {
    dispatch(userActions.logout());
  }, [dispatch]);

  const handleToggleCollapse = useCallback(() => {
    setCollapsed((s) => !s);
  }, []);

  const handleProfileClick = useCallback(() => {
    router.push('/ho-so');
    if (router.pathname.includes('/ho-so')) dispatch(userActions.self.setShowProfileSider(true));
  }, [router, dispatch]);

  return (
    <>
      <div className={headerClasses}>
        <header className="container-2xl w-full flex justify-between items-center px-4 py-2 md:py-3 backdrop-blur-sm">
          <Link href="/">
            <Image
              priority
              src={theme === 'light' ? '/icons/logo_header.svg' : '/icons/logo_header_white.svg'}
              width={160}
              height={60}
              alt="Logo"
            />
          </Link>
          <If
            condition={isMobile}
            Then={
              <div className="flex gap-3">
                <If
                  condition={showAuthButtons}
                  Then={
                    <div className="flex items-center gap-2 cursor-pointer">
                      <Avatar
                        className="border-gray-200"
                        size={32}
                        src={user?.avatar || null}
                        onClick={handleProfileClick}
                      >
                        {user?.name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase()}
                      </Avatar>
                    </div>
                  }
                />
                <div onClick={handleToggleCollapse} className="cursor-pointer block xl:hidden">
                  <IconMenu color={theme === 'light' ? '#000' : '#fff'} />
                </div>
              </div>
            }
            Else={
              <>
                <div className="hidden xl:flex items-center gap-10 border border-slate-100 rounded-full relative">
                  {items.map((menu, idx) => (
                    <Link key={menu.key} href={menu.href} legacyBehavior>
                      <a
                        ref={(el) => (itemRefs.current[idx] = el)}
                        className={menuItemClasses(isActive(menu.key))}
                      >
                        {menu.label}
                      </a>
                    </Link>
                  ))}

                  <If
                    condition={isActive(router.pathname)}
                    Then={
                      <div
                        className="absolute h-8 z-0 rounded-full bg-orange-600 transition-transform duration-300"
                        style={{
                          width: `${activeBar.width}px`,
                          transform: `translateX(${activeBar.x}px)`,
                        }}
                      />
                    }
                  />
                </div>

                <If
                  condition={!showAuthButtons}
                  Then={
                    <Button
                      className={classNames('!h-10 !rounded-md font-bold !bg-orange-600 border-0')}
                      onClick={handleOpenAuthModal}
                    >
                      <span className="text-white font-bold">Đăng nhập</span>
                    </Button>
                  }
                  Else={
                    <div className="flex items-center gap-2 cursor-pointer">
                      <Avatar
                        className="border-gray-200"
                        size={44}
                        src={user?.avatar || null}
                        onClick={() => router.push('/ho-so')}
                      >
                        {user?.name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase()}
                      </Avatar>
                      <div className="flex flex-col">
                        <span className="typography-h5 font-semibold text-gray-900 max-w-[200px] line-clamp-1">
                          {user?.name || user?.email}
                        </span>
                        <div className="flex items-center">
                          <button
                            className="flex items-center gap-1 text-blue-700 text-xs font-medium"
                            onClick={handleLogout}
                          >
                            <LogOut strokeWidth={3} className="w-3 h-3" /> Đăng xuất
                          </button>
                        </div>
                      </div>
                    </div>
                  }
                />
              </>
            }
          />
        </header>
        <MenuMobile items={items} collapsed={collapsed} setCollapsed={setCollapsed} />
      </div>
      <div style={contentStyle} />
    </>
  );
};

export default memo(MainHeaderContent);
